import Validator from 'validatorjs';
import React from 'react';
import $alerting from 'app/stores/alerting/$alerting';
import {
  ADAPTIVE_FLOWSPEC_TEMPLATE_MATCHES,
  ADAPTIVE_FLOWSPEC_TEMPLATE_ACTIONS,
  ADAPTIVE_FLOWSPEC_INFER_OPTIONS,
  ADAPTIVE_FLOWSPEC_GLOBAL_INFER_OPTIONS,
  ADAPTIVE_FLOWSPEC_TRAFFIC_METRICS,
  MITIGATION_TYPES,
  MITIGATION_METHOD_TO_POLICY_DIMENSION_MAP
} from '@kentik/ui-shared/mitigations/constants';
import {
  arrayToCommaSeparatedString,
  commaSeparatedStringToArray,
  arrayToLineSeparatedString,
  lineSeparatedStringToArray
} from 'app/util/utils';
import { isIpV4Valid, isIpV6Valid } from 'core/util/ip';

const BITS_16 = 2 ** 16; // 65536
const BITS_32 = 2 ** 32; // 4294967296

Validator.register('v4IpCidr', (value) => isIpV4Valid(value), 'Enter a valid IPv4/CIDR. ');

Validator.register('v6IpCidr', (value) => isIpV6Valid(value), 'Enter a valid IPv6/CIDR. ');

Validator.register(
  'validCommunity',
  (value) => {
    const re = new RegExp('^[0-9]+:[0-9]+$');
    return re.test(value);
  },
  'Enter a valid Community format. '
);

Validator.register(
  'validExtendedCommunity',
  (value) => {
    const re = new RegExp('^(target|origin):[0-9]+:[0-9]+$');
    return re.test(value);
  },
  'Enter a valid Extended Community format. '
);

Validator.register(
  'validLargeCommunity',
  (value) => {
    const re = new RegExp('^[0-9]+:[0-9]+:[0-9]+$');
    return re.test(value);
  },
  'Enter a valid Large Community format. '
);

Validator.register(
  'validPortRanges',
  (values) => {
    const re = new RegExp('^[0-9]+(-[0-9]+)?(/(UDP|TCP|udp|tcp))?$');
    return values.every((value) => {
      const isValid = re.test(value);
      const [ports] = value.split('/'); // May be number, may be range
      const portRange = ports.split('-');
      const portA = Number(portRange[0]);
      const isValidPortA = portA >= 0 && portA <= BITS_16;

      if (portRange[1]) {
        const portB = Number(portRange[1]);
        return isValid && isValidPortA && portB > portA && portB <= BITS_16;
      }

      return isValid && isValidPortA;
    });
  },
  'Enter one valid port or range per line. '
);

Validator.register(
  'validAsnRouteTarget',
  (value) => {
    const re = new RegExp('^[0-9]+:[0-9]+$');

    // Invalid format
    if (!re.test(value)) {
      return false;
    }

    // Should be in format <asn>:<target>
    const targetValues = value.split(':');
    const [asn, target] = [Number(targetValues[0]), Number(targetValues[1])];
    const isInBoundsAsn = asn > 0 && asn < BITS_32;
    let isValidPair = false;

    if (asn <= BITS_16) {
      isValidPair = target <= BITS_32;
    }

    if (asn > BITS_16) {
      isValidPair = target <= BITS_16;
    }

    return isInBoundsAsn && isValidPair;
  },
  'Enter a valid ASN and Route Target pair. Example: <asn>:<target> '
);

Validator.register(
  'customValidCommunityFormat',
  (values) => {
    let valid = true;
    values = values.split(',');
    values.forEach((value) => {
      if (value.split(':').length !== 2) {
        valid = false;
      }
    });
    return valid;
  },
  'The community must be formatted correctly.'
);

Validator.register(
  'customValidCommunityASN',
  (values) => {
    let valid = true;
    values = values.split(',');
    values.forEach((value) => {
      value = value.split(':');
      value.forEach((community) => {
        // for @ansmith
        const parsed_int = parseInt(community);
        const is_nan = Number.isNaN(parsed_int);
        const number_is_finite = Number.isFinite(parsed_int);
        if (is_nan || !number_is_finite || parsed_int < 0 || parsed_int > 65535) {
          valid = false;
        }
      });
    });

    return valid;
  },
  'Communities should be represented as 16bit-number:16bit-number.'
);

Validator.register(
  'flowspecMatchChildren',
  function flowspecMatchChildren(value) {
    if (value) {
      const fieldNameSplit = this.validator.input.fieldName.split('.');
      const prefix = fieldNameSplit[fieldNameSplit.indexOf(MITIGATION_TYPES.FLOWSPEC) + 1];
      const isIP = prefix === 'sourceIpCidr' || prefix === 'destIpCidr';
      const isInfer = this.validator.input.flowspec[`${prefix}`].infer;

      if (isInfer) {
        return true;
      }

      if (isIP) {
        return this.validator.input.flowspec[`${prefix}`].value;
      }

      const hasMatches = this.validator.input.flowspec[`${prefix}`].value.predicates.length;
      const hasRange = this.validator.input.flowspec[`${prefix}`].value.groups.length;

      return hasMatches || hasRange;
    }

    return true;
  },
  'No values defined'
);

Validator.register(
  'doesNotInvalidateExistingPoliciesUsingMethod',
  function doesNotInvalidateExistingPoliciesUsingMethod(isInferred) {
    if (isInferred) {
      const fieldNameSplit = this.validator.input.fieldName.split('.');
      const prefix = fieldNameSplit[fieldNameSplit.indexOf(MITIGATION_TYPES.FLOWSPEC) + 1];
      const policies = $alerting.policyCollection.get();

      if (!isInferred || !policies.length) {
        return true;
      }

      if (policies.length) {
        // find policies that are using this flowspec method
        // if any exist and have no dimension matching the thing we're about to try to infer here, show an error

        // first filter policies to the ones using the method
        const policiesUsingMethod = policies.filter((policy) =>
          (policy.get('thresholds') || []).find(
            (threshold) =>
              threshold.mitigations.length > 0 &&
              threshold.mitigations.find((mitigation) => mitigation.methodID === this.validator.input.model.id)
          )
        );

        // once we found them, check if they have the required matching dimension for what the method is trying to infer
        if (policiesUsingMethod.length > 0) {
          const invalidatedPolicies = policiesUsingMethod.filter((policyUsingMethod) => {
            const policyDimensions = policyUsingMethod.get('dimensions') || [];

            if (!policyDimensions.includes(MITIGATION_METHOD_TO_POLICY_DIMENSION_MAP[prefix])) {
              return true;
            }

            return false;
          });

          return invalidatedPolicies.length === 0;
        }
      }

      return true;
    }

    return true;
  },
  'Some of the policies that use this method do not contain this dimension. '
);

Validator.register(
  'flowspecDetails',
  function flowspecDetails(value) {
    const {
      destIpCidr,
      sourceIpCidr,
      protocols,
      srcPorts,
      dstPorts,
      icmpTypes,
      icmpCodes,
      tcpFlags,
      packetLengths,
      dscps,
      fragments
    } = value;

    if (this.validator.input.type !== MITIGATION_TYPES.FLOWSPEC) {
      return true;
    }

    return (
      destIpCidr?.enabled ||
      sourceIpCidr?.enabled ||
      protocols?.enabled ||
      srcPorts?.enabled ||
      dstPorts?.enabled ||
      icmpTypes?.enabled ||
      icmpCodes?.enabled ||
      tcpFlags?.enabled ||
      packetLengths?.enabled ||
      dscps?.enabled ||
      fragments?.enabled
    );
  },
  'At least one Traffic Matching Detail must be defined.'
);

// Adaptive Flowspec Validators

Validator.register(
  'adaptiveFlowspecMatchChildren',
  function adaptiveFlowspecMatchChildren(value) {
    const { fieldName, type } = this.validator.input;

    if (type === MITIGATION_TYPES.ADAPTIVE_FLOWSPEC && value) {
      const matchKey = fieldName.split('.')[3];
      const {
        vals = [],
        groups = [],
        infer: matchInfer
      } = this.validator.input[MITIGATION_TYPES.ADAPTIVE_FLOWSPEC]?.template?.matches?.[matchKey] || {};
      const { infer: defaultInfer } = this.validator.input[MITIGATION_TYPES.ADAPTIVE_FLOWSPEC].template;

      const isInferred =
        (defaultInfer === 'FS_INFER_ENABLED' && matchInfer === 'FS_INFER_UNSPECIFIED') ||
        matchInfer === 'FS_INFER_ENABLED';

      return vals.length || groups.length || isInferred;
    }

    return true;
  },
  'At least one Traffic Matching condition is required. '
);

Validator.register(
  'atLeastOneAlgorithmAdaptiveFlowspec',
  function atLeastOneAlgorithmAdaptiveFlowspec(value) {
    const { type } = this.validator.input;
    return type !== MITIGATION_TYPES.ADAPTIVE_FLOWSPEC || value.some((algoItem) => algoItem.enabled);
  },
  'At least one algorithm must be enabled. '
);

Validator.register(
  'atLeastOneOptionEnabledAdaptiveFlowspec',
  function atLeastOneOptionEnabledAdaptiveFlowspec(options) {
    const { type } = this.validator.input;
    const optionKeys = Object.keys(options);
    return type !== MITIGATION_TYPES.ADAPTIVE_FLOWSPEC || optionKeys.some((optionKey) => options[optionKey].enabled);
  },
  'At least one option must be enabled. '
);

const options = {
  name: 'Alert Mitigation Method',
  groups: {
    general: ['name', 'description', 'notificationChannels', 'ackRequired', 'excludedIpCidrs', 'gracePeriod']
  }
};

function getFlowSpecFields(prefix, label, placeholder, addInfer) {
  const flowspecFields = {};
  const fieldPrefix = `flowspec.${prefix}`;
  const isPort = prefix === 'srcPorts' || prefix === 'dstPorts';
  const baseRules = [
    {
      requiredIfMultiple: [`flowspec.${prefix}.enabled`, true, ...(addInfer ? [`flowspec.${prefix}.infer`, false] : [])]
    }
  ];

  const rules = [...baseRules];

  if (isPort) {
    rules.push('port');
  } else if (prefix === 'icmpCodes') {
    rules.push('integer', 'min:0', 'max:15');
  } else if (prefix === 'packetLengths') {
    rules.push('integer', 'min:0', 'max:65535');
  }

  flowspecFields[`${fieldPrefix}.enabled`] = {
    label: <strong>{label}s</strong>,
    defaultValue: false,
    showPristineErrors: false,
    rules: 'flowspecMatchChildren'
  };

  if (addInfer) {
    flowspecFields[`${fieldPrefix}.infer`] = {
      label: 'Infer from Alarm',
      defaultValue: false,
      showPristineErrors: false,
      rules: 'doesNotInvalidateExistingPoliciesUsingMethod'
    };
  }

  flowspecFields[`${fieldPrefix}.value.predicates`] = {
    isComplexArray: true,
    label
  };
  flowspecFields[`${fieldPrefix}.value.predicates[].operator`] = {
    showPristineErrors: false,
    label: `${label} Operator`,
    rules: baseRules
  };
  flowspecFields[`${fieldPrefix}.value.predicates[].value`] = {
    showPristineErrors: false,
    label: `${label} Value`,
    rules,
    placeholder
  };
  flowspecFields[`${fieldPrefix}.value.groups`] = {
    isComplexArray: true,
    label
  };
  flowspecFields[`${fieldPrefix}.value.groups[].predicates`] = {
    isComplexArray: true,
    label
  };
  flowspecFields[`${fieldPrefix}.value.groups[].predicates[].operator`] = {
    showPristineErrors: false,
    label: `${label} Range Value`,
    rules: baseRules
  };
  flowspecFields[`${fieldPrefix}.value.groups[].predicates[].value`] = {
    showPristineErrors: false,
    label: `${label} Range Value`,
    rules
  };
  return flowspecFields;
}

function getAdaptiveFlowspecMatchesFields({ value: prefix, label, operators, placeholder, canInfer }) {
  const matchesFields = {};
  const fieldPrefix = `${MITIGATION_TYPES.ADAPTIVE_FLOWSPEC}.template.matches.${prefix}`;
  const isPort = prefix.includes('_PORT');

  const rules = [];

  if (isPort) {
    rules.push('port');
  } else if (prefix === 'FS_MATCH_TYPE_ICMP_CODE') {
    rules.push('integer', 'min:0', 'max:15');
  } else if (prefix === 'FS_MATCH_TYPE_PKT_LEN') {
    rules.push('integer', 'min:0', 'max:65535');
  } else if (prefix.endsWith('4_PREFIX')) {
    rules.push('v4IpCidr');
  } else if (prefix.endsWith('6_PREFIX')) {
    rules.push('v6IpCidr');
  }

  matchesFields[`${fieldPrefix}.enabled`] = {
    label: <strong>{label}</strong>,
    defaultValue: false,
    showPristineErrors: true,
    rules: 'adaptiveFlowspecMatchChildren'
  };

  if (canInfer) {
    matchesFields[`${fieldPrefix}.infer`] = {
      label: 'Infer from Alarm',
      options: ADAPTIVE_FLOWSPEC_INFER_OPTIONS,
      defaultValue: 'FS_INFER_UNSPECIFIED',
      showPristineErrors: false
    };
  }
  matchesFields[`${fieldPrefix}.vals`] = {
    isComplexArray: true,
    label
  };
  if (operators) {
    matchesFields[`${fieldPrefix}.vals[].op`] = {
      showPristineErrors: false,
      options: operators,
      defaultValue: operators[0].value,
      label: `${label} Operator`
    };
  }
  matchesFields[`${fieldPrefix}.vals[].val`] = {
    showPristineErrors: false,
    label: `${label} Value`,
    rules,
    placeholder,
    messages: { required_if: 'A value is required. Complete or delete this condition. ' },
    transform: {
      out: (value) => value.trim()
    }
  };

  matchesFields[`${fieldPrefix}.groups`] = {
    isComplexArray: true
  };

  matchesFields[`${fieldPrefix}.groups[].vals`] = {
    isComplexArray: true,
    label
  };

  if (operators) {
    matchesFields[`${fieldPrefix}.groups[].vals[].op`] = {
      showPristineErrors: false,
      label: 'Group Condition Operator',
      options: operators,
      defaultValue: operators[0].value
    };
  }
  matchesFields[`${fieldPrefix}.groups[].vals[].val`] = {
    showPristineErrors: false,
    rules,
    label: 'Group Condition Value',
    messages: { required_if: 'A value is required. Complete or delete this condition.  ' },
    transform: {
      out: (value) => value.trim()
    }
  };
  return matchesFields;
}

// Add a param here about whether it's a boolean or has input + rules
function getAdaptiveFlowspecActionsFields({ label, value, placeholder, boolean }) {
  const actionsFields = {};
  const fieldPrefix = `${MITIGATION_TYPES.ADAPTIVE_FLOWSPEC}.template.actions.${value}`;
  const rules = [{ requiredIfMultiple: [`${fieldPrefix}.enabled`, true] }];
  const messages = {};

  if (value.includes('IP4')) {
    rules.push('ipv4', 'ipNoCidr');
    messages.ipNoCidr = 'CIDRs are not supported.';
  } else if (value.includes('IP6')) {
    rules.push('ipv6', 'ipNoCidr');
    messages.ipNoCidr = 'CIDRs are not supported.';
  }

  if (value.includes('_COMMUNITY')) {
    rules.push('validCommunity');
  } else if (value.includes('_LARGE_COMM')) {
    rules.push('validLargeCommunity');
  } else if (value.includes('_EXT_COMM')) {
    rules.push('validExtendedCommunity');
  }

  if (value === 'FS_ACTION_TYPE_REDIRECT') {
    rules.push('validAsnRouteTarget');
  }

  if (value === 'FS_ACTION_TYPE_MARK') {
    rules.push('integer');
    rules.push('min:0');
    rules.push('max:64');
  }

  if (value === 'FS_ACTION_TYPE_RATE_BYTES') {
    rules.push('integer');
    rules.push('min:9600');
    rules.push('max:1000000000000');
  }

  actionsFields[`${fieldPrefix}.enabled`] = {
    label,
    defaultValue: value === 'FS_ACTION_TYPE_DISCARD'
  };

  if (!boolean) {
    actionsFields[`${fieldPrefix}.val`] = {
      label: `${label} Value`,
      rules,
      placeholder,
      messages: {
        required_if: ':attribute is required. ',
        ...messages
      }
    };
  }

  return actionsFields;
}

function getAlgorithmFields() {
  const algorithmFields = {};
  const fieldPrefix = `${MITIGATION_TYPES.ADAPTIVE_FLOWSPEC}.algo[]`;

  // General Fields

  algorithmFields[`${fieldPrefix}.type`] = {
    label: 'Algorithm Type'
  };

  algorithmFields[`${fieldPrefix}.enabled`] = {
    label: 'Enable',
    defaultValue: false
  };

  // Top Ports Algo Fields

  algorithmFields[`${fieldPrefix}.noproto`] = {
    label: 'Ignore Protocol',
    defaultValue: false
  };

  algorithmFields[`${fieldPrefix}.traffic`] = {
    label: 'Minimum Traffic Volume',
    helpText: 'Skip ports below given traffic percentage. ',
    defaultValue: 1,
    rules: ['integer', 'min:0', 'max:100'],
    showPristineErrors: true
  };

  algorithmFields[`${fieldPrefix}.exclude_port`] = {
    label: 'Exclude Ports and Ranges',
    rules: 'validPortRanges',
    helpText: 'Enter one port/range per line to white-list it. Ex: 0-1024 or 0-1024/TCP or 80/UDP',
    transform: {
      in: arrayToLineSeparatedString,
      out: lineSeparatedStringToArray
    }
  };

  // Top CIDRs Algo Fields

  algorithmFields[`${fieldPrefix}.v4budget`] = {
    label: 'IPv4 Budget',
    helpText: 'Max number of IPv4 addresses to block. ',
    rules: ['integer', 'min:0', 'max:32'],
    defaultValue: 4
  };

  algorithmFields[`${fieldPrefix}.v6budget`] = {
    label: 'IPv6 Budget',
    helpText: 'Max number of IPv6 addresses to block. ',
    rules: ['integer', 'min:0', 'max:128'],
    defaultValue: 16
  };

  algorithmFields[`${fieldPrefix}.exclude_ip`] = {
    label: 'Exclude CIDRs',
    rules: 'commaSeparatedIPsCIDR',
    helpText: 'Enter a comma-separated list of CIDRs. ',
    transform: {
      in: arrayToCommaSeparatedString,
      out: commaSeparatedStringToArray
    }
  };
  return algorithmFields;
}

const fields = {
  type: {
    label: ''
  },
  name: {
    label: 'Name',
    rules: 'required|min:2|regex:/^[-\\w\\s]*$/|string',
    placeholder: ''
  },
  description: {
    label: 'Description',
    placeholder: 'Optional'
  },
  notificationChannels: {
    label: 'Notification Channels',
    defaultValue: []
  },
  ackRequired: {
    label: 'Acknowledgement Required',
    defaultValue: false
  },
  excludedIpCidrs: {
    label: 'IPs/CIDRs Excluded From Mitigation (comma-separated)',
    rules: 'commaSeparatedIPsCIDR',
    transform: {
      in: arrayToCommaSeparatedString,
      out: commaSeparatedStringToArray
    }
  },
  gracePeriod: {
    label: 'Grace Period (mins)',
    rules: 'integer',
    defaultValue: 30
  },

  // Radware Fields
  'radware.protocol': {
    label: 'Protocol',
    defaultValue: 'udp',
    options: [
      {
        value: 'tcp',
        label: 'TCP'
      },
      {
        value: 'udp',
        label: 'UDP'
      },
      {
        value: 'icmp',
        label: 'ICMP'
      },
      {
        value: 'other',
        label: 'Other'
      }
    ]
  },
  'radware.useProtocolFromDim': {
    label: 'Use Protocol From Alert Dimension When Available',
    defaultValue: true
  },
  'radware.convertTo24Block': {
    label: 'Ensure at least /24',
    defaultValue: false
  },
  'radware.protectedObjectName': {
    label: 'Protected Object Name',
    defaultValue: '',
    placeholder: 'Optional'
  },
  'radware.icmpBytes.policyID': {
    label: 'ICMP Bytes/second',
    defaultValue: 0
  },
  'radware.icmpPackets.policyID': {
    label: 'ICMP Packets/second',
    defaultValue: 0
  },
  'radware.tcpBytes.policyID': {
    label: 'TCP Bytes/second',
    defaultValue: 0
  },
  'radware.tcpPackets.policyID': {
    label: 'TCP Packets/second',
    defaultValue: 0
  },
  'radware.udpBytes.policyID': {
    label: 'UDP Bytes/second',
    defaultValue: 0
  },
  'radware.udpPackets.policyID': {
    label: 'UDP Packets/second',
    defaultValue: 0
  },

  // RTBH Fields
  'rtbh.communitiesToAdvertise': {
    label: 'Community To Advertise',
    rules: [
      { requiredIfMultiple: ['type', MITIGATION_TYPES.RTBH] },
      'customValidCommunityFormat',
      'customValidCommunityASN'
    ]
  },
  'rtbh.convertTo24Block': {
    label: 'Ensure at least /24',
    defaultValue: false
  },
  'rtbh.nextHopV4': {
    label: 'Next Hop (IPv4)',
    rules: [{ requiredIfMultiple: ['rtbh.nextHopV6', '', 'type', MITIGATION_TYPES.RTBH] }, 'ipNoCidr'],
    messages: {
      required_if: 'One or both of an IPv4 or IPv6 address are required'
    }
  },
  'rtbh.nextHopV6': {
    label: 'Next Hop (IPv6)',
    rules: [{ requiredIfMultiple: ['rtbh.nextHopV4', '', 'type', MITIGATION_TYPES.RTBH] }, 'ipNoCidr'],
    messages: {
      required_if: 'One or both of an IPv4 or IPv6 address are required'
    }
  },
  'rtbh.localPreference': {
    label: 'Local Preference',
    defaultValue: 100,
    // Math.pow(2,32)-1 === 4294967295
    rules: 'integer|min:0|max:4294967295'
  },

  // A10 Fields
  'a10.priority': {
    label: 'Priority',
    defaultValue: 3,
    options: [
      {
        value: 1,
        label: '1 (lowest)'
      },
      {
        value: 2,
        label: '2'
      },
      {
        value: 3,
        label: '3'
      },
      {
        value: 4,
        label: '4'
      },
      {
        value: 5,
        label: '5 (highest)'
      }
    ]
  },
  'a10.mode': {
    label: 'A10 Mode',
    defaultValue: 'static',
    options: [
      {
        value: 'static',
        label: 'Static Configuration'
      },
      {
        value: 'dynamic',
        label: 'Dynamic Configuration'
      }
    ]
  },

  // A10 Static Fields
  'a10.static.bgpAnnounceViaNetworkStatement.enabled': {
    label: 'Announce Via BGP Network Statement',
    defaultValue: true
  },
  'a10.static.bgpAnnounceViaNetworkStatement.ensureAtLeast24': {
    label: 'Announce Via BGP Network Statement',
    defaultValue: true
  },

  // A10 Dynamic Fields
  'a10.dynamic.zoneName': {
    label: 'Zone Name',
    rules: [{ requiredIfMultiple: ['type', MITIGATION_TYPES.A10, 'a10.mode', 'dynamic'] }]
  },
  'a10.dynamic.bgpAdvertiseInZone.enabled': {
    label: 'BGP Advertise In Zone',
    defaultValue: true
  },
  'a10.dynamic.bgpAdvertiseInZone.ensureAtLeast24': {
    label: 'BGP Advertise In Zone',
    defaultValue: true
  },
  'a10.dynamic.bgpAnnounceViaNetworkStatement.enabled': {
    label: 'BGP Announce Via Network Statement',
    defaultValue: true
  },
  'a10.dynamic.bgpAnnounceViaNetworkStatement.ensureAtLeast24': {
    label: 'BGP Announce Via Network Statement',
    defaultValue: true
  },
  macroOptions: {
    defaultValue: 'static',
    options: [
      {
        value: 'static',
        label: 'Static'
      },
      {
        value: 'baseline',
        label: 'Alert Baseline'
      }
    ]
  },
  'a10.dynamic.macros': {
    isComplexArray: true
  },
  'a10.dynamic.macros[].type': {
    defaultValue: 'static'
  },
  'a10.dynamic.macros[].searchAnomaly': {
    label: 'Search Anomaly',
    transform: {
      out: (value) => `${value}`
    }
  },
  'a10.dynamic.macros[].replacementValue': {
    label: 'Replacement Value',
    transform: {
      out: (value) => `${value}`
    }
  },
  'a10.dynamic.macros[].baselineMultiplier': {
    label: 'Multiplier',
    transform: {
      out: (value) => `${value}`
    }
  },
  'a10.dynamic.macros[].baselineOffset': {
    label: 'Offset',
    transform: {
      out: (value) => `${value}`
    }
  },
  'a10.dynamic.macros[].baselineFallbackValue': {
    label: 'Fallback Replacement Value',
    transform: {
      out: (value) => `${value}`
    }
  },
  'a10.dynamic.macros[].policyID': {
    label: 'External Alert Policy'
  },

  // Flowspec Fields
  flowspec: {
    rules: 'flowspecDetails'
  },
  'flowspec.sourceIpCidr.enabled': {
    label: <strong>Source IP/CIDR</strong>,
    defaultValue: false,
    rules: 'flowspecMatchChildren'
  },
  'flowspec.sourceIpCidr.value': {
    showPristineErrors: false,
    label: 'Source IP/CIDR',
    rules: 'ip'
  },
  'flowspec.sourceIpCidr.infer': {
    label: 'Infer from Alarm',
    defaultValue: false,
    rules: 'doesNotInvalidateExistingPoliciesUsingMethod'
  },
  'flowspec.destIpCidr.enabled': {
    label: <strong>Destination IP/CIDR</strong>,
    defaultValue: false,
    rules: 'flowspecMatchChildren'
  },
  'flowspec.destIpCidr.value': {
    showPristineErrors: false,
    label: 'Destination IP/CIDR',
    rules: 'ip'
  },
  'flowspec.destIpCidr.infer': {
    label: 'Infer from Alarm',
    defaultValue: false,
    rules: 'doesNotInvalidateExistingPoliciesUsingMethod'
  },

  ...getFlowSpecFields('protocols', 'Protocol', undefined, true),
  ...getFlowSpecFields('srcPorts', 'Source Port', undefined, true),
  ...getFlowSpecFields('dstPorts', 'Destination Port', undefined, true),
  ...getFlowSpecFields('icmpTypes', 'ICMP Type'),
  ...getFlowSpecFields('icmpCodes', 'ICMP Code'),
  ...getFlowSpecFields('tcpFlags', 'TCP Flag'),
  ...getFlowSpecFields('packetLengths', 'Packet Length', 'Packet length in Bytes'),
  ...getFlowSpecFields('dscps', 'DSCP'),
  ...getFlowSpecFields('fragments', 'Fragment'),

  'flowspec.trafficAction': {
    label: 'Traffic Action',
    rules: [{ required_if: ['type', MITIGATION_TYPES.FLOWSPEC] }],
    helpText: 'Action that flowspec receivers should take on matching packets',
    options: [
      { label: 'Rate Limit', value: 'rateLimit' },
      { label: 'Discard', value: 'discard' },
      { label: 'Mark DSCP', value: 'markDSCP' },
      { label: 'Route-Target Redirect', value: 'routeTargetRedirect' },
      { label: 'Next-Hop Redirect', value: 'nextHopRedirect' }
    ]
  },
  // discard doesn't require extra options but the api needs an "empty" options object
  'flowspec.trafficActions.discard.empty': {
    // There is only 1 valid value and it's null. Convert that to an empty string for inputGroup
    transform: {
      in: () => '',
      out: () => null
    },
    defaultValue: null
  },
  'flowspec.trafficActions.rateLimit.value': {
    label: 'Rate Limit',
    rules: [{ required_if: ['flowspec.trafficAction', 'rateLimit'] }, 'integer', 'min:1'],
    messages: {
      required_if: 'Rate Limit is required when Traffic Action is set to Rate Limit.'
    }
  },
  'flowspec.trafficActions.markDSCP.mark': {
    label: 'Mark DSCP',
    rules: [{ required_if: ['flowspec.trafficAction', 'markDSCP'] }],
    messages: {
      required_if: 'Mark DSCP is required when Traffic Action is set to Mark DSCP.'
    },
    defaultValue: '0'
  },
  'flowspec.trafficActions.routeTargetRedirect.routeTarget': {
    label: 'Route Target',
    rules: [{ required_if: ['flowspec.trafficAction', 'routeTargetRedirect'] }],
    messages: {
      required_if: 'Route Target is required when Traffic Action is set to Route-Target Redirect.'
    }
  },
  'flowspec.trafficActions.nextHopRedirect': {
    label: 'Next-Hop',
    rules: [{ required_if: ['flowspec.trafficAction', 'nextHopRedirect'] }]
  },

  'flowspec.trafficActions.nextHopRedirect.nextHopV4': {
    label: 'Next Hop (IPv4)',
    rules: [
      {
        requiredIfMultiple: [
          'flowspec.trafficAction',
          'nextHopRedirect',
          'flowspec.trafficActions.nextHopRedirect.nextHopV6',
          ''
        ]
      },
      'ipNoCidr'
    ],
    placeholder: 'Next Hop (IPv4)',
    messages: {
      required_if: 'One or both of an IPv4 or IPv6 address are required'
    }
  },
  'flowspec.trafficActions.nextHopRedirect.nextHopV6': {
    label: 'Next Hop (IPv6)',
    rules: [
      {
        requiredIfMultiple: [
          'flowspec.trafficAction',
          'nextHopRedirect',
          'flowspec.trafficActions.nextHopRedirect.nextHopV4',
          ''
        ]
      },
      'ipNoCidr'
    ],
    placeholder: 'Next Hop (IPv6)',
    messages: {
      required_if: 'One or both of an IPv4 or IPv6 address are required'
    }
  },

  // Adaptive Flowspec Fields
  [`${MITIGATION_TYPES.ADAPTIVE_FLOWSPEC}.goal`]: {},
  // All goal percentages must be converted to range of (0.0, 1.0)
  [`${MITIGATION_TYPES.ADAPTIVE_FLOWSPEC}.goal.traffic`]: {
    label: 'Traffic Volume',
    helpText: 'Minimum percentage of policy-matched traffic to block.',
    rules: 'integer|min:0|max:100',
    defaultValue: 80
  },
  [`${MITIGATION_TYPES.ADAPTIVE_FLOWSPEC}.goal.metric`]: {
    label: 'Traffic Metric',
    options: ADAPTIVE_FLOWSPEC_TRAFFIC_METRICS,
    defaultValue: ADAPTIVE_FLOWSPEC_TRAFFIC_METRICS[0].value
  },
  [`${MITIGATION_TYPES.ADAPTIVE_FLOWSPEC}.goal.rules`]: {
    label: 'Max Flowspec Rules',
    helpText: 'Maximum number of Flowspec rules to propagate to each router, per mitigation.',
    rules: 'integer|min:10|max:10000',
    defaultValue: 100
  },
  [`${MITIGATION_TYPES.ADAPTIVE_FLOWSPEC}.algo`]: {
    isComplexArray: true,
    label: 'Algorithms',
    rules: 'atLeastOneAlgorithmAdaptiveFlowspec'
  },
  ...getAlgorithmFields(),

  [`${MITIGATION_TYPES.ADAPTIVE_FLOWSPEC}.failsafe`]: {
    label: 'Deploy failsafe rules if goal not reached',
    defaultValue: true
  },
  [`${MITIGATION_TYPES.ADAPTIVE_FLOWSPEC}.template`]: {},
  [`${MITIGATION_TYPES.ADAPTIVE_FLOWSPEC}.template.infer`]: {
    label: 'Default Infer from Alarm',
    options: ADAPTIVE_FLOWSPEC_GLOBAL_INFER_OPTIONS,
    defaultValue: 'FS_INFER_ENABLED'
  },
  [`${MITIGATION_TYPES.ADAPTIVE_FLOWSPEC}.template.matches`]: {
    label: 'Traffic Matching'
  },

  ...Object.assign({}, ...ADAPTIVE_FLOWSPEC_TEMPLATE_MATCHES.map(getAdaptiveFlowspecMatchesFields)),

  [`${MITIGATION_TYPES.ADAPTIVE_FLOWSPEC}.template.actions`]: {
    label: 'Traffic Filtering Actions',
    rules: 'atLeastOneOptionEnabledAdaptiveFlowspec',
    messages: {
      atLeastOneOptionEnabledAdaptiveFlowspec: 'At least one Traffic Filtering Action must be enabled. '
    }
  },

  ...Object.assign({}, ...ADAPTIVE_FLOWSPEC_TEMPLATE_ACTIONS.map(getAdaptiveFlowspecActionsFields))
};

export { options, fields };
